import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Diamond, Pause, Play } from "lucide-react"
import WorkflowGrid from "./WorkflowGrid"
import { modalityWorkflows } from "@/data/modalityWorkflows"

export default function MultimodalSection() {
  const [activeTab, setActiveTab] = useState("chat")
  const [isAutoCycling, setIsAutoCycling] = useState(true)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    // Check if device is mobile
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  useEffect(() => {
    // Auto-cycle through tabs (disabled on mobile)
    if (isAutoCycling && !isMobile) {
      const tabs = ["chat", "voice", "video", "image", "form", "streaming"]
      const interval = setInterval(() => {
        setActiveTab((current) => {
          const currentIndex = tabs.indexOf(current)
          const nextIndex = (currentIndex + 1) % tabs.length
          return tabs[nextIndex]
        })
      }, 8000) // Switch every 8 seconds

      return () => clearInterval(interval)
    }
  }, [isAutoCycling, isMobile])

  const toggleAutoCycle = () => {
    setIsAutoCycling(!isAutoCycling)
  }

  return (
    <section
      className="container backdrop-blur-lg -backdrop-brightness-200 mx-auto px-4 py-16 pb-12 bg-purple-100/20 rounded-xl my-8"
      id="features"
    >
      <div className="text-center mb-6">
        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Multimodal Capabilities</h2>
        <p className="text-purple-800 max-w-2xl mx-auto">
          Build workflows that seamlessly integrate different types of data and interactions
        </p>
      </div>

        {!isMobile && (
            <div className="margin-auto flex items-center px-4">
              <button
                onClick={toggleAutoCycle}
                className="mx-auto flex items-center text-purple-700 hover:text-black cursor-pointer text-sm bg-purple-200/50 px-3 py-1 rounded-full"
              >
                {isAutoCycling ? (
                  <>
                    <Pause className="h-3 w-3 mr-1" /> Pause Demo
                  </>
                ) : (
                  <>
                    <Play className="h-3 w-3 mr-1" /> Auto Demo
                  </>
                )}
              </button>
            </div>
          )}

      <div className="max-w-5xl min-h-[780px] mt-4 mx-auto">
        <div className="border-b border-purple-700 mb-8">
          <div className="flex items-center flex-wrap gap-2 md:gap-0">
            <button
              id="chat-tab"
              className={`px-3 md:px-6 py-3 text-sm md:text-base text-purple-800 hover:text-black cursor-pointer border-b-2 ${
                activeTab === "chat" ? "border-purple-500 text-purple-900" : "border-transparent"
              } font-medium transition-colors duration-300`}
              onClick={() => {
                setActiveTab("chat")
                setIsAutoCycling(false)
              }}
            >
              Chat
            </button>
            <button
              id="voice-tab"
              className={`px-3 md:px-6 py-3 text-sm md:text-base text-purple-800 hover:text-black cursor-pointer border-b-2 ${
                activeTab === "voice" ? "border-purple-500 text-purple-900" : "border-transparent"
              } font-medium transition-colors duration-300`}
              onClick={() => {
                setActiveTab("voice")
                setIsAutoCycling(false)
              }}
            >
              Voice
            </button>
            <button
              id="video-tab"
              className={`px-3 md:px-6 py-3 text-sm md:text-base text-purple-800 hover:text-black cursor-pointer border-b-2 ${
                activeTab === "video" ? "border-purple-500 text-purple-900" : "border-transparent"
              } font-medium transition-colors duration-300`}
              onClick={() => {
                setActiveTab("video")
                setIsAutoCycling(false)
              }}
            >
              Video
            </button>
            <button
              id="image-tab"
              className={`px-3 md:px-6 py-3 text-sm md:text-base text-purple-800 hover:text-black cursor-pointer border-b-2 ${
                activeTab === "image" ? "border-purple-500 text-purple-900" : "border-transparent"
              } font-medium transition-colors duration-300`}
              onClick={() => {
                setActiveTab("image")
                setIsAutoCycling(false)
              }}
            >
              Image
            </button>
            <button
              id="form-tab"
              className={`px-3 md:px-6 py-3 text-sm md:text-base text-purple-800 hover:text-black cursor-pointer border-b-2 ${
                activeTab === "form" ? "border-purple-500 text-purple-900" : "border-transparent"
              } font-medium transition-colors duration-300`}
              onClick={() => {
                setActiveTab("form")
                setIsAutoCycling(false)
              }}
            >
              Form Triggers
            </button>
            <button
              id="streaming-tab"
              className={`px-3 md:px-6 py-3 text-sm md:text-base text-purple-800 hover:text-black cursor-pointer border-b-2 ${
                activeTab === "streaming" ? "border-purple-500 text-purple-900" : "border-transparent"
              } font-medium transition-colors duration-300`}
              onClick={() => {
                setActiveTab("streaming")
                setIsAutoCycling(false)
              }}
            >
              Streaming
            </button>
          </div>
        </div>

        <div
          id="chat-content"
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-opacity duration-500 ${
            activeTab === "chat" ? "opacity-100" : "opacity-0 hidden"
          }`}
        >
          <div>
            <h3 className="text-2xl font-bold text-black mb-4">Chat Interface</h3>
            <p className="text-black text-lg mb-6">                  
              Create workflows you can chat with, and easily deploy as embeddable widgets for your website.
            </p>
            <p className="text-black text-lg mb-6">
              Build chatbots and customer support agents that aren't one dimensional: easily add any modality you want, calling any tools you want.
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-purple-900 mr-2">✓</span>
                <span className="text-purple-900">Streaming outputs for your users</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-900 mr-2">✓</span>
                <span className="text-purple-900">Integrate with any LLM provider</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-900 mr-2">✓</span>
                <span className="text-purple-900">Customize your deployment's chat widgets (or build your own interface!)</span>
              </li>
            </ul>
            <Button className="bg-purple-600 hover:bg-purple-500 text-white">Try Chat Example</Button>

            <WorkflowGrid 
              title="Popular Chat Workflows" 
              workflows={modalityWorkflows.chat}
              max={2}
            />
          </div>
          <div className="bg-white/95 p-6 rounded-xl border border-purple-300 shadow-lg flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
              <div className="flex items-center">
                <Diamond className="h-5 w-5 text-purple-400 mr-2" />
                <span className="font-medium text-gray-800">Sunflower Stores Assistant</span>
              </div>
              <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700 hover:bg-gray-100">
                <span className="sr-only">Close</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-x"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </Button>
            </div>
            <div className="flex-1 overflow-hidden space-y-4 mb-4">
              <div className="flex items-start">
                <div className="w-8 h-8 rounded-full bg-purple-700 flex items-center justify-center mr-3 flex-shrink-0">
                  <Diamond className="h-4 w-4 text-purple-300" />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg rounded-tl-none p-3 text-gray-800 max-w-[80%] shadow-sm">
                  Hello! I'm your Assistant. How can I help you today?
                </div>
              </div>
              <div className="flex items-start justify-end">
                <div className="bg-purple-100 border border-purple-200 rounded-lg rounded-tr-none p-3 text-gray-800 max-w-[80%] shadow-sm">
                  I need to process some customer feedback data and generate insights.
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 rounded-full bg-purple-700 flex items-center justify-center mr-3 flex-shrink-0">
                  <Diamond className="h-4 w-4 text-purple-300" />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg rounded-tl-none p-3 text-gray-800 max-w-[80%] shadow-sm">
                  I can help with that! Would you like me to create a workflow that analyzes sentiment, extracts key
                  topics, and generates a summary report?
                </div>
              </div>
              <div className="flex items-start justify-end">
                <div className="bg-purple-100 border border-purple-200 rounded-lg rounded-tr-none p-3 text-gray-800 max-w-[80%] shadow-sm">
                  Yes, that sounds perfect. Can you also visualize the results?
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 rounded-full bg-purple-700 flex items-center justify-center mr-3 flex-shrink-0">
                  <Diamond className="h-4 w-4 text-purple-300" />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg rounded-tl-none p-3 text-gray-800 max-w-[80%] shadow-sm">
                  I'll add visualization nodes to create charts for sentiment trends and topic distribution. Your
                  workflow is being prepared now.
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center">
                <input
                  type="text"
                  placeholder="Type your message..."
                  className="mr-2 flex-1 bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg shadow-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-send"
                  >
                    <path d="m22 2-7 20-4-9-9-4Z" />
                    <path d="M22 2 11 13" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          id="voice-content"
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-opacity duration-500 ${
            activeTab === "voice" ? "opacity-100" : "opacity-0 hidden"
          }`}
        >
          <div>
            <h3 className="text-2xl font-bold text-black mb-4">Voice Processing</h3>
            <p className="text-gray-700 mb-6">
              Build voice-enabled applications with our audio processing nodes. Create voice assistants,
              transcription services, and audio analysis tools with ease.
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Real-time speech recognition</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Text-to-speech capabilities</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Voice sentiment analysis</span>
              </li>
            </ul>
            <Button className="bg-purple-600 hover:bg-purple-500 text-white">Try Voice Example</Button>
            
            <WorkflowGrid 
              title="Popular Voice Workflows" 
              workflows={modalityWorkflows.voice} 
            />
          </div>
          <div className="bg-white/95 p-6 rounded-xl border border-purple-300 shadow-lg flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
              <div className="flex items-center">
                <Diamond className="h-5 w-5 text-purple-400 mr-2" />
                <span className="font-medium text-gray-800">Voice Assistant</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-purple-500 animate-pulse"></div>
                <span className="text-gray-600 text-sm">Listening</span>
              </div>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-full h-32 mb-6 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex items-center justify-center space-x-1">
                    <div className="w-1 h-8 bg-purple-500 animate-pulse rounded-full"></div>
                    <div className="w-1 h-16 bg-purple-400 animate-pulse rounded-full"></div>
                    <div className="w-1 h-24 bg-purple-500 animate-pulse rounded-full"></div>
                    <div className="w-1 h-20 bg-purple-400 animate-pulse rounded-full"></div>
                    <div className="w-1 h-12 bg-purple-500 animate-pulse rounded-full"></div>
                    <div className="w-1 h-16 bg-purple-400 animate-pulse rounded-full"></div>
                    <div className="w-1 h-8 bg-purple-500 animate-pulse rounded-full"></div>
                    <div className="w-1 h-12 bg-purple-400 animate-pulse rounded-full"></div>
                    <div className="w-1 h-20 bg-purple-500 animate-pulse rounded-full"></div>
                    <div className="w-1 h-16 bg-purple-400 animate-pulse rounded-full"></div>
                    <div className="w-1 h-8 bg-purple-500 animate-pulse rounded-full"></div>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <p className="text-gray-700 mb-4">
                  "Schedule a meeting with the design team for tomorrow at 2 PM"
                </p>
                <p className="text-gray-800 font-medium">Processing request...</p>
              </div>
            </div>
            <div className="border-t border-gray-200 mt-4 pt-4 flex justify-center">
              <Button className="bg-purple-600 hover:bg-purple-500 text-white rounded-full w-12 h-12">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-send"
                >
                  <path d="m22 2-7 20-4-9-9-4Z" />
                  <path d="M22 2 11 13" />
                </svg>
              </Button>
            </div>
          </div>
        </div>

        <div
          id="video-content"
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-opacity duration-500 ${
            activeTab === "video" ? "opacity-100" : "opacity-0 hidden"
          }`}
        >
          <div>
            <h3 className="text-2xl font-bold text-black mb-4">Video Processing</h3>
            <p className="text-gray-700 mb-6">
              Process and analyze video content with our powerful video processing nodes. Create captioning
              services, content moderation tools, and video analytics solutions.
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Automatic video captioning</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Object and scene detection</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Content summarization</span>
              </li>
            </ul>
            <Button className="bg-purple-600 hover:bg-purple-500 text-white">Try Video Example</Button>
            
            <WorkflowGrid 
              title="Popular Video Workflows" 
              workflows={modalityWorkflows.video} 
            />
          </div>
          <div className="bg-white/95 p-6 rounded-xl border border-purple-300 shadow-lg flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
              <div className="flex items-center">
                <Diamond className="h-5 w-5 text-purple-400 mr-2" />
                <span className="font-medium text-gray-800">Video Captioning</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-gray-600 text-sm">Processing</span>
              </div>
            </div>
            <div className="flex-1 flex flex-col">
              <div className="relative w-full h-48 bg-purple-900/70 rounded-lg mb-4 overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-purple-300 opacity-50"
                  >
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gray-800/90 p-2 text-white text-center">
                  "AI is transforming how we interact with technology"
                </div>
                <div className="absolute top-2 right-2 bg-gray-800/70 text-gray-200 text-xs px-2 py-1 rounded">
                  00:32 / 02:15
                </div>
              </div>
              <div className="space-y-3 flex-1">
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg shadow-sm">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>00:12</span>
                    <span>Confidence: 98%</span>
                  </div>
                  <p className="text-gray-800 text-sm">
                    "Today we're exploring how artificial intelligence is changing..."
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg shadow-sm">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>00:18</span>
                    <span>Confidence: 96%</span>
                  </div>
                  <p className="text-gray-800 text-sm">"...the way we build and interact with technology."</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg shadow-sm">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>00:24</span>
                    <span>Confidence: 99%</span>
                  </div>
                  <p className="text-gray-800 text-sm">
                    "With tools like Lavender Flow, creating AI workflows is easier than ever."
                  </p>
                </div>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" className="text-gray-600 border-gray-300 hover:bg-gray-50">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-1"
                    >
                      <path d="M19 12H5" />
                      <path d="M12 19V5" />
                    </svg>
                    Add Caption
                  </Button>
                  <Button variant="outline" size="sm" className="text-gray-600 border-gray-300 hover:bg-gray-50">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-1"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Export
                  </Button>
                </div>
                <Button size="sm" className="bg-purple-600 hover:bg-purple-500 text-white">
                  Process Video
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div
          id="image-content"
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-opacity duration-500 ${
            activeTab === "image" ? "opacity-100" : "opacity-0 hidden"
          }`}
        >
          <div>
            <h3 className="text-2xl font-bold text-black mb-4">Image Generation</h3>
            <p className="text-gray-700 mb-6">
              Create and manipulate images with our image processing nodes. Build image generation services, style
              transfer applications, and visual content creation tools.
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Text-to-image generation</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Image editing and manipulation</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Visual style transfer</span>
              </li>
            </ul>
            <Button className="bg-purple-600 hover:bg-purple-500 text-white">Try Image Example</Button>
            
            <WorkflowGrid 
              title="Popular Image Workflows" 
              workflows={modalityWorkflows.image} 
            />
          </div>
          <div className="bg-white/95 p-6 rounded-xl border border-purple-300 shadow-lg flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
              <div className="flex items-center">
                <Diamond className="h-5 w-5 text-purple-400 mr-2" />
                <span className="font-medium text-gray-800">Image Generator</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-purple-500 animate-pulse"></div>
                <span className="text-gray-600 text-sm">Generating</span>
              </div>
            </div>
            <div className="flex-1 flex flex-col">
              <div className="mb-4">
                <label className="block text-gray-700 text-sm mb-1">Prompt</label>
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-2 text-gray-800 text-sm">
                  A futuristic city with purple neon lights and floating buildings in a cyberpunk style
                </div>
              </div>
              <div className="flex-1 relative bg-purple-900/30 rounded-lg overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-full bg-gradient-to-br from-purple-800/20 via-purple-600/20 to-purple-400/20 animate-pulse"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="inline-block w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                      <p className="text-gray-700">Generating image...</p>
                      <p className="text-gray-500 text-sm mt-1">Step 24/50</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2">
                <div className="bg-gray-100 border border-gray-300 rounded p-1 cursor-pointer hover:bg-gray-200 transition-colors">
                  <div className="w-full h-12 bg-gray-200 rounded"></div>
                </div>
                <div className="bg-gray-100 border border-gray-300 rounded p-1 cursor-pointer hover:bg-gray-200 transition-colors">
                  <div className="w-full h-12 bg-gray-200 rounded"></div>
                </div>
                <div className="bg-gray-100 border border-gray-300 rounded p-1 cursor-pointer hover:bg-gray-200 transition-colors">
                  <div className="w-full h-12 bg-gray-200 rounded"></div>
                </div>
                <div className="bg-gray-100 border border-gray-300 rounded p-1 cursor-pointer hover:bg-gray-200 transition-colors">
                  <div className="w-full h-12 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          id="form-content"
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-opacity duration-500 ${
            activeTab === "form" ? "opacity-100" : "opacity-0 hidden"
          }`}
        >
          <div>
            <h3 className="text-2xl font-bold text-black mb-4">Form Triggers</h3>
            <p className="text-gray-700 mb-6">
              Create interactive forms that trigger AI workflows. Build data collection interfaces, content
              generation tools, and user-driven automation systems.
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Custom form builders</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Workflow trigger integration</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Real-time form validation</span>
              </li>
            </ul>
            <Button className="bg-purple-600 hover:bg-purple-500 text-white">Try Form Example</Button>
            
            <WorkflowGrid 
              title="Popular Form Workflows" 
              workflows={modalityWorkflows.form} 
            />
          </div>
          <div className="bg-white/95 p-6 rounded-xl border border-purple-300 shadow-lg flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
              <div className="flex items-center">
                <Diamond className="h-5 w-5 text-purple-400 mr-2" />
                <span className="font-medium text-gray-800">Content Generator</span>
              </div>
              <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700 hover:bg-gray-100">
                <span className="sr-only">Close</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-x"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm mb-1">Content Type</label>
                  <select className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500">
                    <option>Blog Post</option>
                    <option>Social Media Post</option>
                    <option>Product Description</option>
                    <option>Email Newsletter</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm mb-1">Topic</label>
                  <input
                    type="text"
                    placeholder="Enter a topic"
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    defaultValue="AI workflow automation"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm mb-1">Tone</label>
                  <div className="flex space-x-2">
                    <button className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm">
                      Professional
                    </button>
                    <button className="px-3 py-1 bg-gray-100 border border-gray-300 text-gray-700 rounded-full text-sm hover:bg-gray-200">
                      Casual
                    </button>
                    <button className="px-3 py-1 bg-gray-100 border border-gray-300 text-gray-700 rounded-full text-sm hover:bg-gray-200">
                      Enthusiastic
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm mb-1">Length</label>
                  <div className="flex items-center">
                    <input type="range" min="1" max="5" defaultValue="3" className="flex-1 accent-purple-500" />
                    <span className="ml-2 text-gray-700 text-sm">Medium</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700 text-sm">Generated Content Preview</span>
                  <span className="text-gray-500 text-xs">Workflow: Content Generator v1.2</span>
                </div>
                <div className="h-16 flex items-center justify-center">
                  <p className="text-gray-500 text-sm italic">Content will appear here after generation</p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-4 flex justify-end">
              <Button className="bg-purple-600 hover:bg-purple-500 text-white">Generate Content</Button>
            </div>
          </div>
        </div>

        <div
          id="streaming-content"
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-opacity duration-500 ${
            activeTab === "streaming" ? "opacity-100" : "opacity-0 hidden"
          }`}
        >
          <div>
            <h3 className="text-2xl font-bold text-black mb-4">Live Streaming</h3>
            <p className="text-gray-700 mb-6">
              Create and manage live streaming workflows with our streaming nodes. Build YouTube, Twitch, and custom
              streaming solutions with real-time AI enhancements.
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Multi-platform streaming (YouTube, Twitch)</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Real-time AI video effects</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Automated content moderation</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-400 mr-2">✓</span>
                <span className="text-gray-700">Live caption generation</span>
              </li>
            </ul>
            <Button className="bg-purple-600 hover:bg-purple-500 text-white">Try Streaming Example</Button>
            
            <WorkflowGrid 
              title="Popular Streaming Workflows" 
              workflows={modalityWorkflows.streaming} 
            />
          </div>
          <div className="bg-white/95 p-6 rounded-xl border border-purple-300 shadow-lg flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
              <div className="flex items-center">
                <Diamond className="h-5 w-5 text-purple-400 mr-2" />
                <span className="font-medium text-gray-800">YouTube Live Stream</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
                <span className="text-gray-600 text-sm">Live</span>
              </div>
            </div>
            <div className="flex-1 flex flex-col">
              <div className="relative w-full h-48 bg-purple-900/70 rounded-lg mb-4 overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-full bg-gradient-to-b from-purple-900/30 to-purple-950/80"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-purple-300 opacity-50 mb-2"
                      >
                        <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
                        <polyline points="17 2 12 7 7 2" />
                      </svg>
                      <p className="text-gray-200">AI Workshop: Building Multimodal Workflows</p>
                    </div>
                  </div>
                </div>
                <div className="absolute top-2 right-2 bg-gray-800/70 text-gray-200 text-xs px-2 py-1 rounded flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mr-1"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                  1,245 viewers
                </div>
                <div className="absolute bottom-2 left-2 bg-gray-800/70 text-gray-200 text-xs px-2 py-1 rounded">
                  01:32:45
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg mb-3 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 text-sm font-medium">Stream Settings</span>
                    <span className="text-green-400 text-xs">Connected to YouTube</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-100 border border-gray-300 p-2 rounded">
                      <span className="block text-gray-500">Resolution</span>
                      <span className="text-gray-800">1080p</span>
                    </div>
                    <div className="bg-gray-100 border border-gray-300 p-2 rounded">
                      <span className="block text-gray-500">Bitrate</span>
                      <span className="text-gray-800">6000 kbps</span>
                    </div>
                    <div className="bg-gray-100 border border-gray-300 p-2 rounded">
                      <span className="block text-gray-500">FPS</span>
                      <span className="text-gray-800">60</span>
                    </div>
                    <div className="bg-gray-100 border border-gray-300 p-2 rounded">
                      <span className="block text-gray-500">Latency</span>
                      <span className="text-gray-800">Low (2s)</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 text-sm font-medium">AI Enhancements</span>
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-green-400 mr-1"></div>
                      <span className="text-green-400 text-xs">Active</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input type="checkbox" className="mr-2 accent-purple-500" defaultChecked />
                      <span className="text-gray-700 text-xs">Live captioning</span>
                    </div>
                    <div className="flex items-center">
                      <input type="checkbox" className="mr-2 accent-purple-500" defaultChecked />
                      <span className="text-gray-700 text-xs">Background blur</span>
                    </div>
                    <div className="flex items-center">
                      <input type="checkbox" className="mr-2 accent-purple-500" />
                      <span className="text-gray-700 text-xs">Voice enhancement</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <Button variant="outline" size="sm" className="text-purple-200 border-purple-700">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mr-1"
                  >
                    <path d="M12 19V5" />
                    <path d="M5 12l7-7 7 7" />
                  </svg>
                  Share
                </Button>
                <Button size="sm" className="bg-red-600 hover:bg-red-500 text-white">
                  End Stream
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
